import React, { useMemo, useEffect, useState } from 'react';
import _ from 'underscore';
import { Callout, Link, mergeStyleSets, Text, MessageBarType, MessageBar } from '@fluentui/react';
import { I18n } from '@bingads-webui-grid/well-known';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { Model$Listing, Model$ProductOffer } from '../../ApiType';
import { useAPIsOfListingGroup } from '../../API';
import { offerLimitExceededWarning } from '../utils';

const styles = mergeStyleSets({
  callout: {
    maxWidth: '90%',
    padding: '20px 24px',
  },
  partitionTrail: {
    fontWeight: 600,
    color: '#605e5c',
  },
  currentOperand: {
    marginBottom: 10,
  },
  currentAttribute: {
    color: '#323130',
    fontSize: '20px',
  },
  productSampleDescription: {
    color: '#323130',
    fontSize: '12px',
    lineHeight: '22px',
    marginBottom: 10,
  },
  productSampleId: {
    fontSize: '14px',
    fontWeight: '600',
  },
  link: {
    display: 'block',
    marginTop: 20,
  },
});

interface ProductOffersCalloutProps {
  target: any;
  i18n: I18n;
  fullPath: Model$Listing[];
  scope: { save: Function, values: any };
  productListingGroupId: number,
}

export const ProductOffersCallout: React.FunctionComponent<ProductOffersCalloutProps> = (props) => {
  const {
    target, i18n, fullPath, scope, productListingGroupId,
  } = props;

  const commonEntityPath = {
    accountId: scope.values.aid,
    campaignId: scope.values.campaignId,
    customerId: scope.values.cid,
    assetGroupId: scope.values.assetGroupId,
  };
  const currentCondition: Model$Listing = fullPath[fullPath.length - 1];
  const currentOperand =
    _.isEmpty(currentCondition.Operand) ? '' : i18n.getString(`ProductsGrid_Column_${currentCondition.Operand}`);
  const partitionTrail = useMemo(() => {
    let trail = i18n.getString(_TL_('All products')).concat(' > ');

    for (let index = 1; index < fullPath.length - 1; index += 1) {
      const item = fullPath[index];

      trail = trail.concat(item.Attribute || '', ' (', i18n.getString(`ProductsGrid_Column_${item.Operand}`), ') > ');
    }
    return trail;
  }, [fullPath, i18n]);

  const {
    getProductOffers,
  } = useAPIsOfListingGroup();
  const [offerCount, setOfferCount] = useState<number>(0);
  const [sampleOffers, setSampleOffers] = useState<Array<Model$ProductOffer>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showOfferLimitExceededError, setShowOfferLimitExceededError] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    getProductOffers({
      entityPath: {
        ...commonEntityPath,
        assetGroupId: scope.values.assetGroupId,
        productListingGroupId,
      },
      queryOptions: {
        $top: 10,
      },
    }).then((response) => {
      setIsLoading(false);

      const offerLimitExceeded = response.headers.get('offerlimitexceeded');

      if (offerLimitExceeded === 'true') {
        setShowOfferLimitExceededError(true);
      } else {
        setOfferCount(response['@odata.count']);
        setSampleOffers(response.value);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productListingGroupId]);

  const navigateToProductTab = () => {
    scope.save('/campaign/vnext/products/');
  };

  const offerLimitExceededContent = useMemo(() => offerLimitExceededWarning(i18n), [i18n]);

  return (
    // eslint-disable-next-line react/jsx-filename-extension
    <>
      <Callout
        className={styles.callout}
        role="dialog"
        gapSpace={0}
        target={target}
        setInitialFocus
      >
        <UiBlocker
          blocking={isLoading}
          loadingText={i18n.getString(_TL_('Loading...'))}
        >
          <Text block className={styles.partitionTrail} >
            {partitionTrail}
          </Text>
          <Text block className={styles.currentOperand} >
            {currentOperand}
          </Text>
          <Text block className={styles.currentAttribute} >
            {currentCondition.Attribute}
          </Text>
          <Text block className={styles.productSampleDescription}>
            {
              offerCount === 0 && !showOfferLimitExceededError &&
              <React.Fragment>{i18n.getString(_TL_('This product group does not contain any products.'))}</React.Fragment>
            }
            {
              offerCount === 1 &&
                <ReactTemplate
                  model={{
                  offerCountModel: text => (<b>{offerCount} {text}</b>),
                }}
                  template={i18n.getString(_TL_('This product group contains {{offerCountModel}} product{{/offerCountModel}}, including:'))}
                />
            }
            {
              offerCount > 1 &&
              <ReactTemplate
                model={{
                offerCountModel: text => (<b>{offerCount} {text}</b>),
              }}
                template={i18n.getString(_TL_('This product group contains {{offerCountModel}} products{{/offerCountModel}}, including:'))}
              />
            }
          </Text>
          {
            offerCount > 0 && sampleOffers.map(sampleOffer => (
              <div>
                <span className={styles.productSampleId}>{ sampleOffer.Id }</span>
                <span> ({sampleOffer.Title})</span>
              </div>
            ))
          }
          {
            offerCount > 0 &&
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link onClick={navigateToProductTab} target="_blank" className={styles.link}>
              {i18n.getString(_TL_('View full list of products'))}
            </Link>
          }
          {
            showOfferLimitExceededError &&
            <MessageBar
              messageBarType={MessageBarType.warning}
            >
              {offerLimitExceededContent}
            </MessageBar>
          }
        </UiBlocker>
      </Callout>
    </>
  );
};
